import { useCallback, useEffect, useMemo, useState } from "react";
import type {
  ContactRef,
  DealFieldDef,
  DealFieldsResponse,
  DealFieldMetaResponse,
  LookupResult,
} from "@edge/shared";
import { DEAL_FIELDS, DEAL_FIELD_GROUPS } from "@edge/shared";
import {
  ApiError,
  createDealContact,
  fetchDealFields,
  fetchDealFieldMeta,
  searchDealLookup,
  updateDealFields,
} from "../../api";
import { WriteConfirm } from "../shared/WriteConfirm";

interface Props {
  dealId: string;
}

interface ContactForm {
  last_name: string;
  first_name?: string;
  email?: string;
  phone?: string;
}

/** A confirmed write: the before→after labels + a closure that performs it. A
 *  "create" runs create-then-link (two Zoho calls) as one gated action. */
interface Pending {
  f: DealFieldDef;
  from: string;
  to: string;
  kind: "field" | "create";
  run: () => Promise<void>;
}

function fmtMoney(n: number): string {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  }).format(n);
}

/** The resolved lookup ref for a lookup field (borrower / co-borrower / lender). */
function lookupRef(apiName: string, data: DealFieldsResponse): ContactRef | null {
  if (apiName === "Contact_Name") return data.borrower;
  if (apiName === "Co_Borrower") return data.coBorrower;
  if (apiName === "Lender") return data.lender;
  return null;
}

/** Read-only display of a field's CURRENT value. */
function displayValue(f: DealFieldDef, data: DealFieldsResponse): string {
  if (f.widget === "lookup") return lookupRef(f.apiName, data)?.name ?? "—";
  if (f.computed) {
    const c = data.computed[f.apiName as keyof DealFieldsResponse["computed"]];
    return c == null ? "—" : f.widget === "currency" ? fmtMoney(c) : `${c}${f.widget === "percent" ? "%" : ""}`;
  }
  return displayRaw(f, data.values[f.apiName]);
}

/** FO-2c: the actionable empty-state prompt for an unlinked editable lookup. */
function emptyLinkPrompt(f: DealFieldDef): string {
  if (f.apiName === "Contact_Name") return "No borrower linked — click to add";
  if (f.apiName === "Co_Borrower") return "+ Add co-borrower";
  return `+ Add ${f.label.toLowerCase()}`;
}

/** Format an arbitrary raw value by widget (used for the before→after gate). */
function displayRaw(f: DealFieldDef, raw: unknown): string {
  if (raw == null || raw === "") return "—";
  if (f.widget === "currency") return fmtMoney(Number(raw));
  if (f.widget === "percent") return `${raw}%`;
  if (f.widget === "boolean") return raw ? "Yes" : "No";
  return String(raw);
}

/** Current raw value → the editor's string draft. */
function toDraft(f: DealFieldDef, data: DealFieldsResponse): string {
  const raw = data.values[f.apiName];
  if (raw == null) return f.widget === "boolean" ? "false" : "";
  if (f.widget === "boolean") return raw ? "true" : "false";
  return String(raw);
}

/** Editor string draft → the raw value sent to the worker. */
function fromDraft(f: DealFieldDef, draft: string): unknown {
  if (f.widget === "boolean") return draft === "true";
  const t = draft.trim();
  if (t === "") return null;
  if (f.widget === "currency" || f.widget === "number" || f.widget === "percent") {
    const n = Number(t);
    return Number.isFinite(n) ? n : null;
  }
  return t;
}

export function DealEditorTab({ dealId }: Props) {
  const [data, setData] = useState<DealFieldsResponse | null>(null);
  const [meta, setMeta] = useState<DealFieldMetaResponse | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [editing, setEditing] = useState<{ apiName: string; draft: string } | null>(null);
  const [pending, setPending] = useState<Pending | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  const load = useCallback(async () => {
    setErr(null);
    try {
      const [d, m] = await Promise.all([fetchDealFields(dealId), fetchDealFieldMeta()]);
      setData(d);
      setMeta(m);
    } catch (e) {
      setErr(e instanceof ApiError ? `${e.code} (HTTP ${e.status})` : "load_failed");
    }
  }, [dealId]);

  useEffect(() => {
    setData(null);
    void load();
  }, [load]);

  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 2600);
    return () => clearTimeout(t);
  }, [toast]);

  const missing = useMemo(() => new Set(meta?.missing ?? []), [meta]);

  if (err) {
    return (
      <div className="tab-content">
        <div className="lme-error">⚠ {err}</div>
      </div>
    );
  }
  if (!data) {
    return (
      <div className="tab-content tab-content-loading">
        <div className="chat-thinking-label">LOADING EDITOR…</div>
      </div>
    );
  }

  const startEdit = (f: DealFieldDef) =>
    setEditing({ apiName: f.apiName, draft: f.widget === "lookup" ? "" : toDraft(f, data) });

  const commitScalar = (f: DealFieldDef) => {
    if (!editing) return;
    const raw = fromDraft(f, editing.draft);
    const cur = data.values[f.apiName] ?? null;
    if (String(raw ?? "") === String(cur ?? "")) {
      setEditing(null); // no change
      return;
    }
    setPending({
      f,
      from: displayValue(f, data),
      to: displayRaw(f, raw),
      kind: "field",
      run: async () => {
        await updateDealFields(dealId, { fields: { [f.apiName]: raw } });
      },
    });
  };

  const pickLookup = (f: DealFieldDef, hit: LookupResult | null) => {
    setEditing(null);
    setPending({
      f,
      from: lookupRef(f.apiName, data)?.name ?? "—",
      to: hit?.name ?? "(unlinked)",
      kind: "field",
      run: async () => {
        await updateDealFields(dealId, { lookups: { [f.apiName]: hit ? hit.id : null } });
      },
    });
  };

  // FO-2c: create a brand-new Contact, then link it — one confirmed action.
  const createLookup = (f: DealFieldDef, form: ContactForm) => {
    setEditing(null);
    const name = [form.first_name?.trim(), form.last_name.trim()].filter(Boolean).join(" ");
    setPending({
      f,
      from: lookupRef(f.apiName, data)?.name ?? "—",
      to: `${name} (new contact)`,
      kind: "create",
      run: async () => {
        const created = await createDealContact(form);
        await updateDealFields(dealId, { lookups: { [f.apiName]: created.id } });
      },
    });
  };

  const renderGroup = (groupKey: DealFieldDef["group"], className?: string) => (
    <div className={"section-block" + (className ? ` ${className}` : "")}>
      <div className="section-title">
        {DEAL_FIELD_GROUPS.find((g) => g.key === groupKey)?.label.toUpperCase()}
      </div>
      {DEAL_FIELDS.filter((f) => f.group === groupKey).map((f) => (
        <EditorRow
          key={f.apiName}
          f={f}
          data={data}
          meta={meta}
          missing={missing.has(f.apiName)}
          isEditing={editing?.apiName === f.apiName}
          draft={editing?.apiName === f.apiName ? editing.draft : ""}
          onStart={() => startEdit(f)}
          onDraft={(d) => setEditing((e) => (e ? { ...e, draft: d } : e))}
          onCommit={() => commitScalar(f)}
          onPick={(hit) => pickLookup(f, hit)}
          onCreate={(form) => createLookup(f, form)}
          onCancel={() => setEditing(null)}
        />
      ))}
    </div>
  );

  return (
    <div className="tab-content">
      <div className="deal-editor-hint">
        ✎ Click a field to edit — each change is confirmed before it writes to Zoho.
        {meta?.source === "fallback" && (
          <span className="deal-editor-warn"> · ⚠ live field options unavailable (using fallback)</span>
        )}
      </div>
      <div className="fo-cols">
        {/* Left: Borrower + Property (the two short, stacked) + Existing Debt */}
        <div className="fo-col">
          {renderGroup("borrower")}
          {renderGroup("property")}
          {renderGroup("existing_debt")}
        </div>
        {/* Right: Loan Request (the long one) + Use & terms + Narrative */}
        <div className="fo-col">
          {renderGroup("loan_request")}
          {renderGroup("use_terms")}
          {renderGroup("narrative")}
        </div>
      </div>

      {pending && (
        <WriteConfirm
          title={pending.kind === "create" ? "Create contact + link in Zoho?" : "Save change to Zoho?"}
          body={<div className="wc-task-subject">{pending.f.label}</div>}
          changes={[{ label: pending.f.label, from: pending.from, to: pending.to }]}
          confirmLabel={pending.kind === "create" ? "CREATE & LINK" : "SAVE TO ZOHO"}
          onConfirm={async () => {
            await pending.run();
            setPending(null);
            setToast(`✓ ${pending.f.label} updated`);
            await load();
          }}
          onCancel={() => setPending(null)}
        />
      )}
      {toast && <div className="edge-toast">{toast}</div>}
    </div>
  );
}

function EditorRow({
  f,
  data,
  meta,
  missing,
  isEditing,
  draft,
  onStart,
  onDraft,
  onCommit,
  onPick,
  onCreate,
  onCancel,
}: {
  f: DealFieldDef;
  data: DealFieldsResponse;
  meta: DealFieldMetaResponse | null;
  missing: boolean;
  isEditing: boolean;
  draft: string;
  onStart: () => void;
  onDraft: (draft: string) => void;
  onCommit: () => void;
  onPick: (hit: LookupResult | null) => void;
  onCreate: (form: ContactForm) => void;
  onCancel: () => void;
}) {
  const isLookup = f.widget === "lookup";
  // Computed + missing never editable; lookups need a module.
  const editable = f.editable && !f.computed && !missing && (!isLookup || !!f.lookupModule);

  if (isEditing) {
    if (isLookup && f.lookupModule) {
      return (
        <div className="data-row deal-editor-row deal-editor-editing">
          <span className="data-label">{f.label}</span>
          <span className="data-value">
            <LookupPicker
              module={f.lookupModule}
              current={lookupRef(f.apiName, data)}
              allowCreate={f.lookupModule === "Contacts"}
              onPick={onPick}
              onCreate={onCreate}
              onCancel={onCancel}
            />
          </span>
        </div>
      );
    }
    const options = meta?.fields[f.apiName]?.options ?? [];
    return (
      <div className="data-row deal-editor-row deal-editor-editing">
        <span className="data-label">{f.label}</span>
        <span className="data-value deal-editor-control">
          {f.widget === "boolean" ? (
            <select className="deal-editor-input" value={draft} onChange={(e) => onDraft(e.target.value)} autoFocus>
              <option value="true">Yes</option>
              <option value="false">No</option>
            </select>
          ) : f.widget === "picklist" ? (
            <select className="deal-editor-input" value={draft} onChange={(e) => onDraft(e.target.value)} autoFocus>
              <option value="">—</option>
              {options.map((o) => (
                <option key={o} value={o}>{o}</option>
              ))}
              {draft && !options.includes(draft) && <option value={draft}>{draft}</option>}
            </select>
          ) : (
            <input
              className="deal-editor-input"
              type={f.widget === "currency" || f.widget === "number" || f.widget === "percent" ? "number" : "text"}
              value={draft}
              autoFocus
              onChange={(e) => onDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") onCommit();
                if (e.key === "Escape") onCancel();
              }}
            />
          )}
          <button className="deal-editor-save" onClick={onCommit} title="Save">✓</button>
          <button className="deal-editor-cancel" onClick={onCancel} title="Cancel">✕</button>
        </span>
      </div>
    );
  }

  // An empty, editable lookup gets a visible "click to add" prompt instead of a
  // silent dash, so an unlinked borrower/co-borrower is obvious + actionable.
  const emptyLookup = isLookup && editable && !lookupRef(f.apiName, data);
  return (
    <div
      className={"data-row deal-editor-row" + (editable ? " deal-editor-editable" : "")}
      onClick={editable ? onStart : undefined}
      title={editable ? "Click to edit" : missing ? "Field not found in Zoho — can't edit" : undefined}
    >
      <span className="data-label">
        {f.label}
        {missing && <span className="deal-editor-missing" title="Not found in Zoho metadata"> ⚠</span>}
      </span>
      <span className="data-value">
        {emptyLookup ? (
          <span className="deal-editor-emptylink">{emptyLinkPrompt(f)}</span>
        ) : (
          displayValue(f, data)
        )}
      </span>
    </div>
  );
}

/** Reusable lookup picker — searches a Zoho module (Contacts/Accounts) and
 *  emits the picked {id,name} (or null to unlink). For Contacts it can also
 *  CREATE a new contact (a separate "+ create" action → mini-form), since a
 *  create needs the optional first/phone/email a search box can't capture. */
function LookupPicker({
  module,
  current,
  allowCreate,
  onPick,
  onCreate,
  onCancel,
}: {
  module: "Contacts" | "Accounts";
  current: ContactRef | null;
  allowCreate?: boolean;
  onPick: (hit: LookupResult | null) => void;
  onCreate: (form: ContactForm) => void;
  onCancel: () => void;
}) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState<LookupResult[]>([]);
  const [busy, setBusy] = useState(false);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState<ContactForm>({ last_name: "" });

  useEffect(() => {
    if (q.trim().length < 2) {
      setResults([]);
      return;
    }
    const h = setTimeout(async () => {
      setBusy(true);
      try {
        const r = await searchDealLookup(module, q.trim());
        setResults(r.results);
      } catch {
        setResults([]);
      } finally {
        setBusy(false);
      }
    }, 300);
    return () => clearTimeout(h);
  }, [q, module]);

  // FO-2c: an already-linked contact shows ONLY Unlink (no search/create
  // clutter). After unlinking, the field empties and the next click shows the
  // search-or-create picker (the empty-state flow). Contacts only — Lender
  // (Accounts) keeps re-pick-while-linked.
  if (current && module === "Contacts") {
    return (
      <div className="lookup-picker lookup-picker-linked">
        <span className="lookup-picker-current">{current.name}</span>
        <button
          className="lookup-picker-unlink"
          onClick={() => onPick(null)}
          title="Unlink this contact"
        >
          Unlink
        </button>
        <button className="deal-editor-cancel" onClick={onCancel} title="Cancel">✕</button>
      </div>
    );
  }

  if (creating) {
    return (
      <div className="lookup-picker lookup-picker-create">
        <input
          className="deal-editor-input"
          autoFocus
          placeholder="First name"
          value={form.first_name ?? ""}
          onChange={(e) => setForm((f) => ({ ...f, first_name: e.target.value }))}
        />
        <input
          className="deal-editor-input"
          placeholder="Last name *"
          value={form.last_name}
          onChange={(e) => setForm((f) => ({ ...f, last_name: e.target.value }))}
        />
        <input
          className="deal-editor-input"
          placeholder="Phone"
          value={form.phone ?? ""}
          onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))}
        />
        <input
          className="deal-editor-input"
          placeholder="Email"
          value={form.email ?? ""}
          onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
        />
        <div className="lookup-picker-create-actions">
          <button className="lookup-picker-back" onClick={() => setCreating(false)}>← back</button>
          <button
            className="deal-editor-save lookup-picker-create-go"
            disabled={!form.last_name.trim()}
            onClick={() =>
              onCreate({
                last_name: form.last_name.trim(),
                first_name: form.first_name?.trim() || undefined,
                phone: form.phone?.trim() || undefined,
                email: form.email?.trim() || undefined,
              })
            }
          >
            Create &amp; link →
          </button>
          <button className="deal-editor-cancel" onClick={onCancel} title="Cancel">✕</button>
        </div>
      </div>
    );
  }

  return (
    <div className="lookup-picker">
      <div className="lookup-picker-bar">
        <input
          className="deal-editor-input lookup-picker-input"
          autoFocus
          placeholder={`Search ${module}…`}
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Escape") onCancel();
          }}
        />
        {current && (
          <button className="lookup-picker-unlink" onClick={() => onPick(null)} title="Remove link">
            unlink
          </button>
        )}
        <button className="deal-editor-cancel" onClick={onCancel} title="Cancel">✕</button>
      </div>
      {(busy || q.trim().length >= 2) && (
        <div className="lookup-picker-results">
          {busy && <div className="lookup-picker-empty">searching…</div>}
          {!busy && results.length === 0 && <div className="lookup-picker-empty">no matches</div>}
          {results.map((r) => (
            <button key={r.id} className="lookup-picker-result" onClick={() => onPick(r)}>
              {r.name}
            </button>
          ))}
        </div>
      )}
      {allowCreate && (
        <button
          className="lookup-picker-createnew"
          onClick={() => {
            // Prefill last name with the search text (common: typed a name, no match).
            setForm({ last_name: q.trim() });
            setCreating(true);
          }}
        >
          + Create new contact
        </button>
      )}
    </div>
  );
}
